/**
 * Which widgets this person's launcher shows (the owner's list of 2026-10-12;
 * the server's `core/shown.py`). The list of hidden keys is the person's, on
 * the server, so the phone agrees.
 *
 * **To hide is a choice of display.** A hidden widget is out of the launcher
 * and the chat strip's list; the agent, a link and an older reply still open
 * it. D23's class is the other thing, and `widgetsFor` holds it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import { api } from "../api/client";
import { ok } from "../api/query";
import { useMe } from "../app/me";
import { widgetsFor, type WidgetEntry } from "../widgets/registry";

// `data_changed` for `widgets` reads it again: the key begins with the area's name.
const HIDDEN = ["widgets", "hidden"] as const;

/** The one widget with no switch: it holds the switches. */
export const ALWAYS_SHOWN = "settings";

export function useHidden() {
  return useQuery({
    queryKey: HIDDEN,
    queryFn: async () => (await ok(api.GET("/api/widgets/hidden"))).hidden,
    staleTime: 5 * 60_000,
  });
}

/** The person's widgets, less the ones they hid. All of them until the list is read. */
export function useShownWidgets(): readonly WidgetEntry[] {
  const me = useMe();
  const hidden = useHidden().data;
  return useMemo(() => {
    const all = widgetsFor(me.owner);
    return hidden?.length ? all.filter((entry) => !hidden.includes(entry.key)) : all;
  }, [me.owner, hidden]);
}

/** Show or hide one widget. The switch moves at once, and goes back if the server refuses. */
export function useSetShown() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ key, shown }: { key: string; shown: boolean }) => {
      const now = client.getQueryData<string[]>(HIDDEN) ?? [];
      const next = shown ? now.filter((k) => k !== key) : [...new Set([...now, key])];
      client.setQueryData(HIDDEN, next);
      return ok(api.PUT("/api/widgets/hidden", { body: { hidden: next } }));
    },
    onSuccess: (kept) => client.setQueryData(HIDDEN, kept.hidden),
    onError: () => client.invalidateQueries({ queryKey: HIDDEN }),
  });
}
